import useTournamentDetails from '@/hooks/useTournamentDetails';
import { format } from 'date-fns';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import TournamentTableDetailsModal from './TournamentTableDetailsModal';
import TableStatusIndicator from './TableStatusIndicator';
import { FaExternalLinkAlt } from 'react-icons/fa';
import { IoDocumentTextOutline } from 'react-icons/io5';
import { config } from '@repo/common-types';
import { TableStatus } from '@/common/types/phenomTypes';

// Table Activity Indicator Component
const TableActivityIndicator = ({ lastHandCompletedAt }: { lastHandCompletedAt?: Date | string | null }) => {
  const getActivityColor = () => {
    if (!lastHandCompletedAt) return '#6c757d'; // gray for no data

    const now = new Date();
    const lastActivity = new Date(lastHandCompletedAt);
    const minutesElapsed = (now.getTime() - lastActivity.getTime()) / (1000 * 60);

    if (minutesElapsed < 2) return '#28a745'; // green
    if (minutesElapsed < 5) return '#ffc107'; // yellow
    if (minutesElapsed < 10) return '#fd7e14'; // orange
    return '#dc3545'; // red
  };

  const getActivityPriority = () => {
    if (!lastHandCompletedAt) return 5; // lowest priority for no data

    const now = new Date();
    const lastActivity = new Date(lastHandCompletedAt);
    const minutesElapsed = (now.getTime() - lastActivity.getTime()) / (1000 * 60);

    if (minutesElapsed < 2) return 1; // green - highest priority
    if (minutesElapsed < 5) return 2; // yellow
    if (minutesElapsed < 10) return 3; // orange
    return 4; // red
  };

  const getTooltipText = () => {
    if (!lastHandCompletedAt) return 'No activity data';

    const now = new Date();
    const lastActivity = new Date(lastHandCompletedAt);
    const minutesElapsed = Math.floor((now.getTime() - lastActivity.getTime()) / (1000 * 60));

    if (minutesElapsed === 0) {
      return 'Hand completed less than a minute ago';
    } else if (minutesElapsed === 1) {
      return 'Hand completed 1 minute ago';
    } else {
      return `Hand completed ${minutesElapsed} minutes ago`;
    }
  };

  const color = getActivityColor();

  return (
    <div
      className="rounded-circle"
      style={{
        width: '10px',
        height: '10px',
        backgroundColor: color,
        boxShadow: `0 0 6px ${color}`,
        position: 'absolute',
        bottom: '8px',
        right: '8px',
      }}
      title={getTooltipText()}
    />
  );
};

const PokerTableDetails = ({ table }: { table: any }) => {
  const [tableDetailsOpen, setTableDetailsOpen] = useState(false);
  const [hovered, setHovered] = useState(false);

  const handleOpenTable = (e) => {
    e.stopPropagation();
    const url = `${config.urls.app}/table/${table?.table.id}`;
    const width = 800;
    const height = 600;
    window.open(url, table?.table.id, `height=${height},width=${width}`);
  };

  const handleOpenTableLogs = (e) => {
    e.stopPropagation();
    const tableId = table?.table?.id;
    if (!tableId) return;
    const base = 'https://telemetry.betterstack.com/team/t238083/tail?s=969735,969739,969749';
    const query = encodeURIComponent(`metadata.tableId=${tableId}`);
    const url = `${base}&q=${query}&rf=now-7d`;
    window.open(url, '_blank');
  };

  return (
    <>
      {tableDetailsOpen && <TournamentTableDetailsModal table={table} onClose={() => setTableDetailsOpen(false)} />}
      <div className="h-100">
        <div
          className="d-flex flex-column bg-dark h-100 position-relative cursor-pointer rounded p-3"
          role="button"
          style={{ transform: hovered ? 'scale(1.02)' : 'scale(1)', transition: 'transform 0.2s' }}
          onMouseEnter={() => setHovered(true)}
          onMouseLeave={() => setHovered(false)}
          onClick={() => setTableDetailsOpen(true)}
        >
          {/* Header */}
          <div className="d-flex justify-content-between align-items-center mb-2">
            <div className="d-flex align-items-center gap-2">
              <button
                onClick={handleOpenTableLogs}
                title="Open table logs"
                className="border-0 bg-transparent p-1 text-white"
                style={{ cursor: 'pointer' }}
                aria-label="Open table logs"
              >
                <IoDocumentTextOutline size={16} />
              </button>
              <h6 className="text-truncate m-0">
                {(() => {
                  if (table?.table?.tableNumber) {
                    return `Table #${table.table.tableNumber}`;
                  }
                  const match = table?.table?.name?.match(/Table #\d+/);
                  return match ? match[0] : table?.table?.name;
                })()}
              </h6>
            </div>
            <div className="d-flex align-items-center gap-3">
              <TableStatusIndicator status={table?.table.status} />
              <button
                onClick={handleOpenTable}
                title="Open table in new window"
                className="border-0 bg-transparent p-1 text-white"
                style={{ cursor: 'pointer' }}
              >
                <FaExternalLinkAlt size={12} />
              </button>
            </div>
          </div>

          {/* Player count info */}
          <div className="mb-2">
            <small className="text-muted">
              Players: {table?.players?.length || 0}/{table?.table?.maxPlayers || 'Unknown'}
            </small>
          </div>

          {/* Players list or empty state */}
          <div className="d-flex flex-column flex-1">
            {table?.players.length === 0 ? (
              <div className="d-flex flex-column justify-content-center align-items-center flex-1 py-3">
                <small className="text-muted">No players at this table</small>
              </div>
            ) : (
              <div className="flex-1">
                {table.players.slice(0, 3).map((player, index) => (
                  <div key={player.userId || index} className="d-flex justify-content-between align-items-center py-1">
                    <small className="text-truncate">{player.alias}</small>
                    <small className="text-muted">{player.stack}</small>
                  </div>
                ))}
                {table.players.length > 3 && <small className="text-muted">+{table.players.length - 3} more...</small>}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="mt-auto pt-2">
            <small className="text-muted">Created at {format(new Date(table?.table.createdAt), 'HH:mm')}</small>
          </div>

          {/* Activity Indicator - positioned bottom right */}
          <TableActivityIndicator lastHandCompletedAt={table?.table?.lastHandCompletedAt} />
        </div>
      </div>
    </>
  );
};

const TournamentTablesDetails = () => {
  const { tournamentId } = useParams();
  const { tournamentTables, refetchTournamentTables } = useTournamentDetails(tournamentId ?? '');

  useEffect(() => {
    const interval = setInterval(() => {
      refetchTournamentTables();
    }, 15000);

    return () => clearInterval(interval);
  }, [refetchTournamentTables]);

  // Calculate responsive column classes based on number of tables
  const getColumnClass = (tableCount: number) => {
    if (tableCount === 1) return 'col-md-6';
    if (tableCount === 2) return 'col-md-6';
    if (tableCount === 3) return 'col-md-4';
    return 'col-xl-3 col-lg-4 col-md-6'; // 4 per row on XL, 3 on LG, 2 on MD
  };

  // Helper function to get activity priority for a table
  const getTableActivityPriority = (table: any) => {
    if (!table?.table?.lastHandCompletedAt) return 5;

    const now = new Date();
    const lastActivity = new Date(table.table.lastHandCompletedAt);
    const minutesElapsed = (now.getTime() - lastActivity.getTime()) / (1000 * 60);

    if (minutesElapsed < 2) return 1; // green
    if (minutesElapsed < 5) return 2; // yellow
    if (minutesElapsed < 10) return 3; // orange
    return 4; // red
  };

  // Sort tables by status priority, then by activity color, then by table number
  const sortedTables =
    tournamentTables?.slice().sort((a, b) => {
      const statusPriority = {
        [TableStatus.RUNNING]: 1,
        [TableStatus.INITIALIZING]: 2,
        [TableStatus.STOPPED]: 3,
        [TableStatus.RESTARTING]: 4,
        [TableStatus.STOPPING]: 5,
      };

      const aStatus = a?.table?.status as TableStatus;
      const bStatus = b?.table?.status as TableStatus;

      const aPriority = aStatus ? statusPriority[aStatus] || 99 : 99;
      const bPriority = bStatus ? statusPriority[bStatus] || 99 : 99;

      // First sort by status priority
      if (aPriority !== bPriority) {
        return aPriority - bPriority;
      }

      // If status is the same, sort by activity color priority
      const aActivityPriority = getTableActivityPriority(a);
      const bActivityPriority = getTableActivityPriority(b);

      if (aActivityPriority !== bActivityPriority) {
        return aActivityPriority - bActivityPriority;
      }

      // Finally, sort by table number
      const aTableNumber = a?.table?.tableNumber || 0;
      const bTableNumber = b?.table?.tableNumber || 0;
      return aTableNumber - bTableNumber;
    }) || [];

  const columnClass = getColumnClass(sortedTables?.length || 0);

  return (
    <div className="d-flex flex-column gap-4">
      <div className="d-flex flex-column gap-2">
        <h4 className="text-lg font-bold">Tables</h4>
        {/* Activity Legend */}
        {sortedTables && sortedTables.length > 0 && (
          <div className="d-flex align-items-center mb-2 gap-3">
            <small className="text-muted">Activity Status:</small>
            <div className="d-flex align-items-center gap-2">
              <div className="d-flex align-items-center gap-1">
                <div className="rounded-circle" style={{ width: '8px', height: '8px', backgroundColor: '#28a745' }} />
                <small className="text-muted">&lt; 2 min</small>
              </div>
              <div className="d-flex align-items-center gap-1">
                <div className="rounded-circle" style={{ width: '8px', height: '8px', backgroundColor: '#ffc107' }} />
                <small className="text-muted">2-5 min</small>
              </div>
              <div className="d-flex align-items-center gap-1">
                <div className="rounded-circle" style={{ width: '8px', height: '8px', backgroundColor: '#fd7e14' }} />
                <small className="text-muted">5-10 min</small>
              </div>
              <div className="d-flex align-items-center gap-1">
                <div className="rounded-circle" style={{ width: '8px', height: '8px', backgroundColor: '#dc3545' }} />
                <small className="text-muted">&gt; 10 min</small>
              </div>
            </div>
          </div>
        )}
        {sortedTables && sortedTables.length > 0 ? (
          <div className="row g-3">
            {sortedTables.map((table, index) => (
              <div key={table?.table?.id || index} className={columnClass}>
                <PokerTableDetails table={table} />
              </div>
            ))}
          </div>
        ) : (
          <div className="text-muted py-4 text-center">
            <p>No tables available for this tournament</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default TournamentTablesDetails;
